import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { GetRootState } from 'js/src/libs/RootState';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import { getSceneDesignDefaultIconUrls } from '../sceneDesignDefaults';
import type { LintFinding } from '../videoLinter';

interface IconAssetLike {
    name: string;
    isEditable: boolean;
    sourceData?: { url?: string };
}

/**
 * Warning: an icon asset still shows the scene design's default icon. Like
 * unchanged placeholder text, a default icon reads as an unfinished template —
 * every icon should be swapped for one relevant to the scene's content.
 */
export function checkPlaceholderIcon(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        const defaults = getSceneDesignDefaultIconUrls(scene);
        if (!defaults || defaults.size === 0) {
            return;
        }
        const nonRecommendable = getNonRecommendableAssetNames(scene, getState());
        const iconAssets = scene.getAssetsByType(
            VideoData.ICON_ASSET_TYPE,
        ) as unknown as IconAssetLike[];
        iconAssets
            .filter((asset) => asset.isEditable && !nonRecommendable.has(asset.name))
            .forEach((asset) => {
                const defaultUrl = defaults.get(asset.name);
                const currentUrl = asset.sourceData?.url;
                if (defaultUrl && currentUrl && defaultUrl === currentUrl) {
                    findings.push({
                        check: 'placeholder_icon',
                        severity: 'warning',
                        sceneId: scene.id,
                        assetName: asset.name,
                        message: `Icon asset "${asset.name}" still shows the design's default icon. Replace it with an icon relevant to this scene's content (${TOOL_NAMES.searchIcons}, then ${TOOL_NAMES.setIcon}) — a default icon reads as an unfinished placeholder.`,
                    });
                }
            });
    });

    return findings;
}
